import React from 'react';
import { Link } from 'react-router-dom';
import Header from './shared/Header';
import Footer from './shared/Footer';
import {
  Bot,
  Server,
  Layers,
  Code2,
  Rocket,
  ArrowRight,
  Database,
} from 'lucide-react';


const networkCapabilities = [
  {
    icon: <Bot size={22} />,
    title: 'AI & ML Engineering',
    description:
      'Engineers who have shipped production AI systems: LLM integrations, fine-tuning, evals, and inference optimization.',
  },
  {
    icon: <Server size={22} />,
    title: 'Infrastructure Architecture',
    description:
      'Architects who design for scale, reliability, and cost: cloud infrastructure, data platforms, and distributed systems.',
  },
  {
    icon: <Layers size={22} />,
    title: 'Product Design for AI',
    description:
      'Designers who understand AI constraints and build interfaces that make intelligent systems feel intuitive to end users.',
  },
  {
    icon: <Rocket size={22} />,
    title: 'Implementation & Delivery',
    description:
      'Delivery leads who keep engagements on track: scoping, sequencing, and surfacing risk before it becomes a problem.',
  },
  {
    icon: <Code2 size={22} />,
    title: 'Full-Stack Engineering',
    description:
      'Engineers who own the full surface: frontend, backend, APIs, and integrations, without handing off at every layer.',
  },
  {
    icon: <Database size={22} />,
    title: 'Data Engineering',
    description:
      'Specialists in pipelines, warehouses, and data modeling who make sure the right data gets to the right place reliably.',
  },
];

const TeamPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Page header */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-16 bg-gradient-to-br from-blue-50 via-white to-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm font-medium mb-6">
            Who we are
          </span>
          <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            A boutique core. A senior network.
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            We operate as a small core team backed by a curated network of senior practitioners
            we assemble based on what your engagement needs.
          </p>
        </div>
      </section>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">

        {/* Section 2: Our Network */}
        <div className="mb-20 md:mb-28">
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">
            Our Network
          </h2>
          <p className="text-gray-600 max-w-2xl mb-10 leading-relaxed">
            A curated network of senior engineers, architects, designers, and delivery leads
            we assemble based on engagement needs.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {networkCapabilities.map((cap) => (
              <div
                key={cap.title}
                className="bg-gray-50 rounded-2xl p-6 border border-gray-100 hover:border-blue-100
                           hover:shadow-sm transition-all"
              >
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center mb-4 text-blue-600">
                  {cap.icon}
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-2">{cap.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{cap.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Section 3: How we assemble teams */}
        <div className="mb-12">
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-6">
            How we assemble teams
          </h2>
          <div className="bg-blue-50 rounded-2xl p-8 md:p-12 max-w-3xl">
            <p className="text-gray-700 text-base md:text-lg leading-relaxed">
              Every engagement gets a small, senior team matched to the scope. We don't staff by
              availability. We staff by fit. That means you get operators who've shipped this kind
              of work before, not junior consultants learning on your dime.
            </p>
          </div>
        </div>

      </div>

      {/* Bottom CTA */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Want to work with us?</h2>
          <Link
            to="/contact"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700
                       text-white rounded-xl font-medium transition-all shadow-md hover:shadow-lg"
          >
            Start a conversation
            <ArrowRight className="ml-2" size={18} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TeamPage;
